import { Component, ElementRef, OnDestroy, computed, effect, inject, signal, untracked, viewChild, ChangeDetectionStrategy } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { DecimalPipe } from '@angular/common';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { toSignal, toObservable } from '@angular/core/rxjs-interop';
import { switchMap, map, catchError, filter, startWith, tap } from 'rxjs/operators';
import { of } from 'rxjs';
import { AuthService } from '../../auth/auth.service';
import { SessionService, QueueSnapshot } from '../../core/session.service';
import { ToastService } from '../../core/services/toast.service';
import { QueueCardComponent } from '../../component/queue-card/queue-card.component';
import { QrModalComponent } from '../../component/qr-modal.component';
import { ConfirmModalComponent } from '../../component/confirm-modal/confirm-modal.component';
import { FilActualiteComponent } from '../fil-actualite/fil-actualite.component';
import { PrevisionsComponent } from '../previsions/previsions.component';
import { ForecastGraphComponent } from '../previsions/forecast-graph/forecast-graph.component';
import { SatisfactionComponent } from '../satisfaction/satisfaction.component';
import { EtablissementComponent } from '../etablissement/etablissement.component';
import { ProfileIaComponent } from '../profile-ia/profile-ia.component';
// GestionEquipesComponent : import retiré (2026-09-08), bloc "Gestion des équipes" masqué --
// voir la note DEPRECATED en tête de site.component.html.
// QualiteComponent : import retiré (2026-09-11), sous-page "Fidélisation patient · Qualité"
// masquée -- voir la note DEPRECATED en tête de hasQualitePermission dans ce fichier.
import { TimezoneService } from '../../core/services/timezone.service';
import { EstablishmentsService, EstablishmentDetailsDto, DayDto, PATIENT_LANGUAGES } from '../../core/services/establishments.service';
import { OrganisationsService, OrganisationDetailsDto } from '../../core/services/organisations.service';
import { API_BASE_URL } from '../../core/api-base-url.token';
import { PatientMessagesService, PatientMessage } from '../../core/services/patient-messages.service';
import { WizardConfigService, QueueConfig } from '../../core/services/wizard-config.service';
import { EquipementService, Equipement } from '../../core/services/equipement.service';
import { ActualiteService, User } from '../../core/services/actualite.service';
import { TeamService } from '../../core/services/team.service';
import { PageSiteSwitcherComponent } from '../../component/page-site-switcher/page-site-switcher.component';
import { SatisfactionService, FeedbackResponse, EstablishmentStats, MonthlySatisfactionPoint, QualityStatsPeriod } from '../../core/services/satisfaction.service';
import { PreventionService, ActivityItem, ActivityPage, PreventionPerformance, PreventionFunnel, EstablishmentOfferCatalog, FunnelAnalytics, PerformanceAnalytics, OfferPerformanceDetail, EstablishmentContact, PatientContactRole, PatientIdentityMode, SiteNeedCoverageSummaryView, CoverageState } from '../../core/services/prevention.service';
import { OFFER_SITE_CURRENCIES } from '../prevention-offer-sites/offer-sites.component';
import { PreventionSiteAddOfferComponent } from '../prevention-site-add-offer/prevention-site-add-offer.component';
import { OfferFormPageComponent } from '../prevention-offer-form/offer-form-page.component';
import { PreventionOffersService } from '../../core/services/prevention-offers.service';
import { PreventionGroupHeaderComponent } from '../../component/prevention-group-header/prevention-group-header.component';
import { resolveAvatarStyle } from '../../core/utils/avatar-color.util';
import { formatLastFrame } from '../../core/utils/last-frame.util';
import { flagIcon } from '../../core/utils/flag-icon.util';
import { lockPageScroll, unlockPageScroll } from '../../layout/shell/shell.component';

type SiteTab = 'comm' | 'sat' | 'prevention' | 'params';

@Component({
  selector: 'app-site',
  standalone: true,
  imports: [QueueCardComponent, QrModalComponent, ConfirmModalComponent, FilActualiteComponent, PrevisionsComponent, ForecastGraphComponent, SatisfactionComponent, EtablissementComponent, ProfileIaComponent, PageSiteSwitcherComponent, PreventionSiteAddOfferComponent, OfferFormPageComponent, PreventionGroupHeaderComponent, DecimalPipe, TranslatePipe],
  templateUrl: './site.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./site.component.scss'],
})
export class SiteComponent implements OnDestroy {
  readonly session = inject(SessionService);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly translate = inject(TranslateService);
  private readonly auth = inject(AuthService);
  private readonly timezone = inject(TimezoneService);
  private readonly estabService = inject(EstablishmentsService);
  private readonly organisationsService = inject(OrganisationsService);
  private readonly baseUrl = inject(API_BASE_URL);
  private readonly patientMessagesSvc = inject(PatientMessagesService);
  private readonly wizardConfigSvc = inject(WizardConfigService);
  private readonly equipementSvc = inject(EquipementService);
  private readonly actualiteSvc = inject(ActualiteService);
  private readonly teamSvc = inject(TeamService);
  private readonly satisfactionSvc = inject(SatisfactionService);
  private readonly preventionSvc = inject(PreventionService);
  private readonly offersSvc = inject(PreventionOffersService);
  private readonly toast = inject(ToastService);

  readonly lastActus = toSignal(
    toObservable(this.session.selectedId).pipe(
      filter((id): id is string => !!id),
      switchMap(id =>
        this.patientMessagesSvc.getMessages(id, 0, 15).pipe(
          map(r => r.content),
          tap(messages => this.fetchActuImages(id, messages)),
          catchError(() => of([] as PatientMessage[]))
        )
      )
    ),
    { initialValue: [] as PatientMessage[] }
  );

  /** Miniatures des messages "Actus / Préventions", chargées à part (endpoint image dédié) plutôt qu'embarquées en base64 dans la liste */
  actuImageUrls = signal<Map<string, string>>(new Map());

  /** Plafonne la hauteur du bloc "Actus / Préventions" à la hauteur naturelle de "Temps réel" */
  private readonly tempsReelCardRef = viewChild<ElementRef<HTMLElement>>('tempsReelCard');
  readonly tempsReelHeight = signal<number | null>(null);

  constructor() {
    effect((onCleanup) => {
      const ref = this.tempsReelCardRef();
      if (!ref) {
        this.tempsReelHeight.set(null);
        return;
      }
      const el = ref.nativeElement;
      this.tempsReelHeight.set(el.offsetHeight);
      const ro = new ResizeObserver(() => this.tempsReelHeight.set(el.offsetHeight));
      ro.observe(el);
      onCleanup(() => ro.disconnect());
    });

    // Resynchronise le brouillon dès qu'un nouveau chargement arrive (changement de site, ou
    // refresh après enregistrement) -- jamais l'inverse, la saisie utilisateur ne doit jamais
    // écraser un rechargement serveur silencieusement.
    effect(() => {
      const cfg = this.contactConfig();
      this.contactRoleDraft.set(cfg?.patientContactRole ?? null);
      this.returnToReceptionDraft.set(!!cfg?.patientReturnToReception);
      this.identityModeDraft.set(cfg?.patientIdentityOnSite ?? 'NONE');
      this.identityAttestationDraft.set(false);
    });

    // Ferme toute édition/suppression d'offre "Offres du site" ouverte dès que l'établissement
    // sélectionné change (BLOCKER #1, revue technique 2026-09-03) : le sélecteur de site en tête de
    // page reste cliquable pendant toute l'édition, sans quoi la ligne éditée resterait affichée
    // avec des valeurs périmées d'un AUTRE établissement. Défense en profondeur -- la garde
    // AUTORITATIVE (comparaison synchrone au clic Enregistrer/Confirmer) reste dans saveOffer/
    // confirmRemoveOffer, indépendante du timing d'exécution de cet effect.
    effect(() => {
      const currentId = this.session.selectedId();
      if (this.editingEstablishmentId() !== null && this.editingEstablishmentId() !== currentId) {
        this.editingOfferId.set(null);
        this.editingEstablishmentId.set(null);
      }
      if (this.removeOfferEstablishmentId() !== null && this.removeOfferEstablishmentId() !== currentId) {
        this.removeOfferTarget.set(null);
        this.removeOfferEstablishmentId.set(null);
        this.removeOfferError.set(null);
        unlockPageScroll();
      }
    });

    // Drill-down Groupe -> Site : sélectionne l'établissement demandé par la route (même mécanisme
    // que l'ancien openSite() de Vue d'ensemble, jamais un second call), et atterrit sur Prévention
    // -- sauf si l'utilisateur n'a pas le droit PREVENTION, auquel cas on laisse le comportement par
    // défaut (onglet Communication) plutôt que de forcer un onglet inaccessible.
    effect(() => {
      const id = this.groupContextEstablishmentId();
      if (!this.groupContext() || !id) return;
      this.session.selectEstablishment(id);
      if (this.hasPreventionPermission) this.activeTab.set('prevention');
    });
  }

  /** Récupère les miniatures des messages "Actus / Préventions" : appelé une seule fois par nouvelle liste de messages (voir `tap` dans `lastActus`), jamais depuis un `effect` pour éviter tout risque de ré-entrée. */
  private fetchActuImages(establishmentId: string, messages: PatientMessage[]) {
    this.revokeActuImageUrls();

    const toFetch = messages.filter(m => !!m.imageContentType);
    if (toFetch.length === 0) return;

    for (const msg of toFetch) {
      this.patientMessagesSvc.getImage(establishmentId, msg.id).subscribe({
        next: (blob) => {
          const url = URL.createObjectURL(blob);
          const next = new Map(this.actuImageUrls());
          next.set(msg.id, url);
          this.actuImageUrls.set(next);
        },
        error: (err) => console.warn('[site] échec du chargement de la miniature du message', msg.id, err)
      });
    }
  }

  /** Libère les blob URLs des miniatures avant de charger celles du nouvel établissement, pour éviter d'accumuler des images en mémoire au fil des changements de site. */
  private revokeActuImageUrls() {
    for (const url of this.actuImageUrls().values()) {
      URL.revokeObjectURL(url);
    }
    this.actuImageUrls.set(new Map());
  }

  getActuImageUrl(messageId: string): string | undefined {
    return this.actuImageUrls().get(messageId);
  }

  ngOnDestroy() {
    this.revokeActuImageUrls();
  }

  private readonly _estabDetailsRefresh = signal(0);
  private readonly _estabDetailsTrigger = computed(() => ({
    id: this.session.selectedId(),
    refresh: this._estabDetailsRefresh(),
  }));

  readonly estabDetails = toSignal(
    toObservable(this._estabDetailsTrigger).pipe(
      switchMap(({ id }) => id
        ? this.estabService.getDetails(id).pipe(catchError(() => of(null)))
        : of(null)
      )
    ),
    { initialValue: null as EstablishmentDetailsDto | null }
  );

  /** Les surcharges optimistes des interrupteurs ne valent que pour le site affiché : à chaque
   *  nouveau détail chargé (changement de site), la valeur serveur reprend la main. */
  private readonly _resetDisplayOverrides = effect(() => {
    this.estabDetails();
    untracked(() => {
      this._patientReasonOverride.set(null);
      this._preventionStartOnLaunchOverride.set(null);
      this._widgetEnabledOverride.set(null);
      this._showForecastOverride.set(null);
      this._showWaitTimeOverride.set(null);
      this._showPatientReplyOverride.set(null);
      this._showFeelingSectionOverride.set(null);
      this._showGoogleReviewOverride.set(null);
      this._showUnderstandCareOverride.set(null);
      this._quizEnabledOverride.set(null);
      this._preventionPointEnabledOverride.set(null);
    });
  });

  /** Logo partagé par tous les sites de l'organisation */
  private readonly _orgDetailsRefresh = signal(0);
  private readonly _orgDetailsTrigger = computed(() => ({
    orgId: this.estabDetails()?.org_id ?? null,
    refresh: this._orgDetailsRefresh(),
  }));

  readonly orgDetails = toSignal(
    toObservable(this._orgDetailsTrigger).pipe(
      switchMap(({ orgId }) => orgId
        ? this.organisationsService.getDetails(orgId).pipe(catchError(() => of(null)))
        : of(null)
      )
    ),
    { initialValue: null as OrganisationDetailsDto | null }
  );

  readonly logoUrl = computed(() => {
    const o = this.orgDetails();
    return o?.has_logo ? this.organisationsService.getLogoUrl(o.id, o.logo_updated_at) : null;
  });

  readonly uploadingLogo = signal(false);

  onLogoSelected(event: Event) {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    input.value = '';
    const orgId = this.estabDetails()?.org_id;
    if (!file || !orgId) return;

    this.uploadingLogo.set(true);
    this.organisationsService.uploadLogo(orgId, file).subscribe({
      next: () => {
        this.uploadingLogo.set(false);
        this._orgDetailsRefresh.update(n => n + 1);
        window.dispatchEvent(new Event('organisationLogoUpdated'));
        this.toast.show('Logo mis à jour', 'success');
      },
      error: (err) => {
        this.uploadingLogo.set(false);
        this.toast.show(err?.error?.message || 'Erreur lors de l\'envoi du logo', 'error');
      }
    });
  }

  readonly removeLogoOpen = signal(false);
  readonly removeLogoLoading = signal(false);
  readonly removeLogoError = signal<string | null>(null);

  openRemoveLogo() {
    this.removeLogoOpen.set(true);
    this.removeLogoError.set(null);
  }

  closeRemoveLogo() {
    this.removeLogoOpen.set(false);
    this.removeLogoLoading.set(false);
    this.removeLogoError.set(null);
  }

  confirmRemoveLogo() {
    const orgId = this.estabDetails()?.org_id;
    if (!orgId) return;
    this.removeLogoLoading.set(true);
    this.removeLogoError.set(null);
    this.organisationsService.deleteLogo(orgId).subscribe({
      next: () => {
        this._orgDetailsRefresh.update(n => n + 1);
        window.dispatchEvent(new Event('organisationLogoUpdated'));
        this.toast.show('Logo supprimé', 'success');
        this.closeRemoveLogo();
      },
      error: () => {
        this.removeLogoLoading.set(false);
        this.removeLogoError.set('Une erreur est survenue. Veuillez réessayer.');
      }
    });
  }

  /** Photo propre à ce site, indépendante du logo d'organisation (partagé, lui, par tous les sites) */
  readonly photoUrl = computed(() => {
    const e = this.estabDetails();
    return e?.has_photo ? this.estabService.getPhotoUrl(e.id, e.photo_updated_at) : null;
  });

  readonly uploadingPhoto = signal(false);

  onPhotoSelected(event: Event) {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    input.value = '';
    const estabId = this.estabDetails()?.id;
    if (!file || !estabId) return;

    this.uploadingPhoto.set(true);
    this.estabService.uploadPhoto(estabId, file).subscribe({
      next: () => {
        this.uploadingPhoto.set(false);
        this._estabDetailsRefresh.update(n => n + 1);
        this.toast.show('Photo mise à jour', 'success');
      },
      error: (err) => {
        this.uploadingPhoto.set(false);
        this.toast.show(err?.error?.message || 'Erreur lors de l\'envoi de la photo', 'error');
      }
    });
  }

  readonly removePhotoOpen = signal(false);
  readonly removePhotoLoading = signal(false);
  readonly removePhotoError = signal<string | null>(null);

  openRemovePhoto() {
    this.removePhotoOpen.set(true);
    this.removePhotoError.set(null);
  }

  closeRemovePhoto() {
    this.removePhotoOpen.set(false);
    this.removePhotoLoading.set(false);
    this.removePhotoError.set(null);
  }

  confirmRemovePhoto() {
    const estabId = this.estabDetails()?.id;
    if (!estabId) return;
    this.removePhotoLoading.set(true);
    this.removePhotoError.set(null);
    this.estabService.deletePhoto(estabId).subscribe({
      next: () => {
        this._estabDetailsRefresh.update(n => n + 1);
        this.toast.show('Photo supprimée', 'success');
        this.closeRemovePhoto();
      },
      error: () => {
        this.removePhotoLoading.set(false);
        this.removePhotoError.set('Une erreur est survenue. Veuillez réessayer.');
      }
    });
  }

  private readonly _patientReasonOverride = signal<boolean | null>(null);
  readonly showPatientReason = computed(
    () => this._patientReasonOverride() ?? this.estabDetails()?.show_patient_reason ?? false
  );

  /** Lance directement le point prévention à l'ouverture de mobileV2 (sans attendre le scan) --
   *  n'a d'effet que si preventionPointEnabled est actif, désactivé par défaut sur tous les sites. */
  private readonly _preventionStartOnLaunchOverride = signal<boolean | null>(null);
  readonly preventionStartOnLaunch = computed(
    () => this._preventionStartOnLaunchOverride() ?? this.estabDetails()?.prevention_point_start_on_launch ?? false
  );
  readonly hasGooglePlace = computed(() => !!(this.estabDetails()?.place_id));

  private readonly _widgetEnabledOverride = signal<boolean | null>(null);
  readonly widgetEnabled = computed(
    () => this._widgetEnabledOverride() ?? this.estabDetails()?.widget_enabled ?? false
  );

  private readonly _showForecastOverride = signal<boolean | null>(null);
  readonly showForecast = computed(
    () => this._showForecastOverride() ?? this.estabDetails()?.show_forecast ?? true
  );

  /** Accueil patient : afficher le temps d'attente. Coupé = prévisions grisées (valeur conservée),
   *  le motif reste réglable (écran motif sans fourchette). TV, widget et API publique inchangés. */
  private readonly _showWaitTimeOverride = signal<boolean | null>(null);
  readonly showWaitTime = computed(
    () => this._showWaitTimeOverride() ?? this.estabDetails()?.show_wait_time ?? true
  );

  private readonly _showPatientReplyOverride = signal<boolean | null>(null);
  readonly showPatientReply = computed(
    () => this._showPatientReplyOverride() ?? this.estabDetails()?.show_patient_reply ?? true
  );

  private readonly _showFeelingSectionOverride = signal<boolean | null>(null);
  readonly showFeelingSection = computed(
    () => this._showFeelingSectionOverride() ?? this.estabDetails()?.show_feeling_section ?? true
  );

  private readonly _showGoogleReviewOverride = signal<boolean | null>(null);
  readonly showGoogleReview = computed(
    () => this._showGoogleReviewOverride() ?? this.estabDetails()?.show_google_review ?? true
  );

  /** Affiche/masque le bouton "Comprendre ma prise en charge" sur la page mobileV2 patient. */
  private readonly _showUnderstandCareOverride = signal<boolean | null>(null);
  readonly showUnderstandCare = computed(
    () => this._showUnderstandCareOverride() ?? this.estabDetails()?.show_understand_care ?? true
  );

  private readonly _quizEnabledOverride = signal<boolean | null>(null);
  readonly quizEnabled = computed(
    () => this._quizEnabledOverride() ?? this.estabDetails()?.quiz_enabled ?? false
  );

  /** Affiche/active le bloc "point prévention" sur la page mobileV2 patient -- désactivé par
   *  défaut sur tous les sites (2026-09-02), même mécanique que quiz_enabled/widget_enabled. */
  private readonly _preventionPointEnabledOverride = signal<boolean | null>(null);
  readonly preventionPointEnabled = computed(
    () => this._preventionPointEnabledOverride() ?? this.estabDetails()?.prevention_point_enabled ?? false
  );

  readonly languesDisplay = computed(() => {
    const labelOf = (code: string) => PATIENT_LANGUAGES.find(l => l.code === code)?.label ?? code;
    return (this.estabDetails()?.langues ?? []).map(code => ({ code, label: labelOf(code) }));
  });

  readonly flagIcon = flagIcon;


  /** weekday (1=Lun...7=Dim, convention DayDto backend) -> code neutre pour common.days.* */
  private static readonly WEEKDAY_CODES = ['', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'];

  private shortDayLabel(weekday: number): string {
    const code = SiteComponent.WEEKDAY_CODES[weekday];
    return this.translate.instant('common.days.shortByCode.' + code);
  }

  formatOpeningHours(hours: DayDto[]): string {
    const open = (hours ?? []).filter(d => d.slots.length > 0).sort((a, b) => a.weekday - b.weekday);
    if (!open.length) return this.translate.instant('site.common.hoursNotConfigured');
    const slot = open[0].slots[0];
    const time = `${slot.start.slice(0, 5)} – ${slot.end.slice(0, 5)}`;
    const days = open.length === 1
      ? this.shortDayLabel(open[0].weekday)
      : `${this.shortDayLabel(open[0].weekday)} → ${this.shortDayLabel(open[open.length - 1].weekday)}`;
    return `${time} · ${days}`;
  }

  readonly wizardDays = ['Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi', 'Dimanche'];
  /** Clé French->code neutre (identifiants "Lundi".. conservés tels quels : utilisés comme clés de lookup
   *  par days.isLow/isNormal/isHigh, pas comme texte affiché — seul le libellé ci-dessous est traduit). */
  private static readonly WIZARD_DAY_CODES: Record<string, string> = {
    Lundi: 'mon', Mardi: 'tue', Mercredi: 'wed', Jeudi: 'thu', Vendredi: 'fri', Samedi: 'sat', Dimanche: 'sun',
  };

  dayShortLabel(wizardDay: string): string {
    const code = SiteComponent.WIZARD_DAY_CODES[wizardDay];
    return this.translate.instant('common.days.shortByCode.' + code);
  }

  // Chargé seulement sur l'onglet Paramètres (seul endroit où wizardConfig est affiché) : évite un
  // appel réseau systématique au chargement de la page alors que l'onglet par défaut est Communication.
  private readonly _wizardConfigTrigger = computed(() =>
    this.activeTab() === 'params' ? this.session.selectedId() : null
  );

  private readonly wizardConfig = toSignal(
    toObservable(this._wizardConfigTrigger).pipe(
      switchMap(id => id
        ? this.wizardConfigSvc.getEstablishmentConfig(id).pipe(
            catchError(() => of([] as QueueConfig[])),
            startWith(null)
          )
        : of(null)
      )
    ),
    { initialValue: null as QueueConfig[] | null }
  );

  readonly wizardConfigLoading = computed(() => this.wizardConfig() === null);

  readonly demandDays = computed(() => {
    const configs = this.wizardConfig();
    if (!configs) return null;
    const demand = configs[0]?.config?.data?.demand;
    if (!demand) return null;
    const high = new Set(demand.high_days ?? []);
    const low  = new Set(demand.low_days  ?? []);
    return {
      isHigh:   (d: string) => high.has(d),
      isLow:    (d: string) => low.has(d),
      isNormal: (d: string) => !high.has(d) && !low.has(d),
    };
  });
  // DEPRECATED (2026-09-08) : orgSnapshot/siteEquipements/hasEquipementsPermission/
  // showEquipementsSubpage/openEquipements/closeEquipements + statutLabel/statutBadge/
  // formatDerniereDonnee (plus bas) alimentaient UNIQUEMENT le bloc "Équipement" masqué dans
  // site.component.html -- à supprimer avec lui. EquipementService reste utilisé par ailleurs
  // (equipements.component.ts, supervision.component.ts pour le temps d'attente réel via la
  // pipeline waitora-capteur) -- ne jamais supprimer le service en entier.
  // NB : la page autonome /equipements (EquipementsComponent, route dans app.routes.ts) et son
  // contrôleur backend EquipementsController restent fonctionnels et accessibles par URL directe
  // -- seule l'entrée du menu sidebar a été retirée (shell.component.html), pas la route elle-même.
  //
  // Snapshot équipements de toute l'organisation : coûteux et affiché seulement sur l'onglet
  // Paramètres (bloc "Équipement") — inutile de le tirer au chargement sur l'onglet Communication.
  private readonly _orgSnapshotTrigger = computed(() => this.activeTab() === 'params');

  private readonly orgSnapshot = toSignal(
    toObservable(this._orgSnapshotTrigger).pipe(
      switchMap(active => active
        ? this.equipementSvc.getOrgSnapshot().pipe(catchError(() => of(null)))
        : of(null)
      )
    ),
    { initialValue: null }
  );

  get hasEquipementsPermission() { return this.auth.hasPermission('EQUIPEMENTS'); }

  get hasUserDroitPermission() { return this.auth.hasPermission('USER_DROIT'); }

  // DEPRECATED (2026-09-11) : hasQualitePermission/showQualiteSubpage/openQualite/closeQualite
  // n'alimentent plus que la sous-page "Fidélisation patient · Qualité" masquée dans
  // site.component.html (bouton "Voir plus" de la carte "Indicateurs qualité" retiré, seul
  // déclencheur de showQualiteSubpage) -- candidats à la suppression complète avec ce bloc quand
  // la fonctionnalité sera vraiment décommissionnée.
  // NE PAS supprimer QualiteComponent (qualite.component.*) ni QualityService
  // (core/services/quality.service.ts) ni les contrôleurs backend associés (QualityController,
  // QualityAnalyticsController, QualityDataController dans waitora-quality) tant que ce périmètre
  // reste actif ailleurs : route autonome /qualite (app.routes.ts) et embed dans
  // statistiques.component.ts, tous deux indépendants de ce bouton.
  // Décommission complète = retirer en même temps : ce bloc + les deux autres entrées front
  // ci-dessus + les contrôleurs/services/repos backend qui ne seraient alors plus appelés.
  get hasQualitePermission() { return this.auth.hasPermission('QUALITE'); }

  get hasPreventionPermission() { return this.auth.hasPermission('PREVENTION'); }

  get hasSatisfactionPermission() { return this.auth.hasPermission('SATISFACTION'); }

  get hasFilActualitePermission() { return this.auth.hasPermission('FIL_ACTUALITE'); }

  readonly siteEquipements = computed((): Equipement[] => {
    const id   = this.session.selectedId();
    const snap = this.orgSnapshot();
    if (!id || !snap) return [];
    return snap.equipements
      .filter(e => e.establishmentId === id && e.active)
      .slice(0, 3);
  });

  // Liste des utilisateurs de l'org (jusqu'à 100), affichée seulement sur l'onglet Paramètres
  // (bloc "Gestion des équipes") — pas de chargement au démarrage sur l'onglet Communication.
  private readonly _allUsersRefresh = signal(0);
  private readonly _allUsersTrigger = computed(() => ({
    active: this.activeTab() === 'params',
    refresh: this._allUsersRefresh(),
  }));

  private readonly allUsers = toSignal(
    toObservable(this._allUsersTrigger).pipe(
      switchMap(({ active }) => active
        ? this.actualiteSvc.getUsersPaginated(0, 100).pipe(
            map(r => r.users),
            catchError(() => of([]))
          )
        : of([] as User[])
      )
    ),
    { initialValue: [] as User[] }
  );
  private refreshAllUsers() {
    this.actualiteSvc.invalidateUsersPagedCache();
    this._allUsersRefresh.update(n => n + 1);
  }

  readonly siteUsers = computed(() => {
    const id = this.session.selectedId();
    if (!id) return [];
    return this.allUsers().filter(u => (u.establishment_ids ?? []).includes(id));
  });

  userInitials(u: User): string {
    const a = (u.first_name?.[0] ?? '').toUpperCase();
    const b = (u.last_name?.[0] ?? '').toUpperCase();
    return `${a}${b}` || '?';
  }

  // Libellés/couleurs des postes, utilisés seulement pour l'affichage des utilisateurs sur
  // l'onglet Paramètres — mêmes raisons que orgSnapshot/allUsers ci-dessus.
  private readonly _jobTitlesTrigger = computed(() => this.activeTab() === 'params');

  private readonly jobTitles = toSignal(
    toObservable(this._jobTitlesTrigger).pipe(
      switchMap(active => active
        ? this.teamSvc.getJobTitles().pipe(catchError(() => of([])))
        : of([])
      )
    ),
    { initialValue: [] }
  );
  private readonly jobTitleColorMap = computed(() => new Map(this.jobTitles().map(j => [j.id, j.color])));
  private readonly jobTitleLabelMap = computed(() => new Map(this.jobTitles().map(j => [j.id, j.label])));

  userAvatarStyle(u: User): Record<string, string> {
    return resolveAvatarStyle(u, id => this.jobTitleColorMap().get(id));
  }

  userJobTitleLabel(u: User): string {
    return (u.job_title_ids ?? []).map(id => this.jobTitleLabelMap().get(id)).filter(Boolean).join(' · ');
  }

  statutLabel(s: Equipement['statut']): string {
    const key = s === 'OK' ? 'site.equipements.status.online' : s === 'Instable' ? 'site.equipements.status.unstable' : 'site.equipements.status.offline';
    return this.translate.instant(key);
  }

  statutBadge(s: Equipement['statut']): string {
    return s === 'OK' ? 'g' : s === 'Instable' ? 'o' : 'r';
  }

  formatDerniereDonnee(derniereTrameAt: string | null): string {
    return formatLastFrame(derniereTrameAt, this.translate);
  }

  readonly togglingReason = signal(false);

  togglePatientReason(): void {
    const id = this.session.selectedId();
    if (!id || this.togglingReason()) return;
    const next = !this.showPatientReason();
    this._patientReasonOverride.set(next);
    this.togglingReason.set(true);
    this.estabService.updateInfo(id, { show_patient_reason: next }).subscribe({
      next: () => {
        this.togglingReason.set(false);
        this.toast.show('C\'est noté. Merci 👋', 'success', 4000, next ? 'Motif de visite activé' : 'Motif de visite désactivé');
      },
      error: () => {
        this._patientReasonOverride.set(!next);
        this.togglingReason.set(false);
        this.toast.show('Erreur', 'error', 4000, 'Impossible de modifier le paramètre');
      },
    });
  }

  readonly togglingPreventionStartOnLaunch = signal(false);

  togglePreventionStartOnLaunch(): void {
    const id = this.session.selectedId();
    if (!id || this.togglingPreventionStartOnLaunch() || !this.preventionPointEnabled()) return;
    const next = !this.preventionStartOnLaunch();
    this._preventionStartOnLaunchOverride.set(next);
    this.togglingPreventionStartOnLaunch.set(true);
    this.estabService.updateInfo(id, { prevention_point_start_on_launch: next }).subscribe({
      next: () => {
        this.togglingPreventionStartOnLaunch.set(false);
        this.toast.show('C\'est noté. Merci 👋', 'success', 4000, next ? 'Point prévention au démarrage activé' : 'Point prévention au démarrage désactivé');
      },
      error: () => {
        this._preventionStartOnLaunchOverride.set(!next);
        this.togglingPreventionStartOnLaunch.set(false);
        this.toast.show('Erreur', 'error', 4000, 'Impossible de modifier le paramètre');
      },
    });
  }

  readonly togglingWidget = signal(false);

  toggleWidget(): void {
    const id = this.session.selectedId();
    if (!id || this.togglingWidget()) return;
    const next = !this.widgetEnabled();
    this._widgetEnabledOverride.set(next);
    this.togglingWidget.set(true);
    this.estabService.updateInfo(id, { widget_enabled: next }).subscribe({
      next: () => {
        this.togglingWidget.set(false);
        this.toast.show('C\'est noté. Merci 👋', 'success', 4000, next ? 'Widget affluence activé' : 'Widget affluence désactivé');
      },
      error: () => {
        this._widgetEnabledOverride.set(!next);
        this.togglingWidget.set(false);
        this.toast.show('Erreur', 'error', 4000, 'Impossible de modifier le paramètre');
      },
    });
  }

  readonly togglingWaitTime = signal(false);

  toggleShowWaitTime(): void {
    const id = this.session.selectedId();
    if (!id || this.togglingWaitTime()) return;
    const next = !this.showWaitTime();
    this._showWaitTimeOverride.set(next);
    this.togglingWaitTime.set(true);
    this.estabService.updateInfo(id, { show_wait_time: next }).subscribe({
      next: () => {
        this.togglingWaitTime.set(false);
        this.toast.show('C\'est noté. Merci 👋', 'success', 4000, next ? 'Temps d\'attente affiché' : 'Temps d\'attente masqué');
      },
      error: () => {
        this._showWaitTimeOverride.set(!next);
        this.togglingWaitTime.set(false);
        this.toast.show('Erreur', 'error', 4000, 'Impossible de modifier le paramètre');
      },
    });
  }

  readonly togglingForecast = signal(false);

  toggleShowForecast(): void {
    const id = this.session.selectedId();
    // Grisé quand l'attente est masquée : la valeur est conservée, le patient ne voit pas les prévisions.
    if (!id || this.togglingForecast() || !this.showWaitTime()) return;
    const next = !this.showForecast();
    this._showForecastOverride.set(next);
    this.togglingForecast.set(true);
    this.estabService.updateInfo(id, { show_forecast: next }).subscribe({
      next: () => {
        this.togglingForecast.set(false);
        this.toast.show('C\'est noté. Merci 👋', 'success', 4000, next ? 'Prévisions activées' : 'Prévisions désactivées');
      },
      error: () => {
        this._showForecastOverride.set(!next);
        this.togglingForecast.set(false);
        this.toast.show('Erreur', 'error', 4000, 'Impossible de modifier le paramètre');
      },
    });
  }

  readonly togglingPatientReply = signal(false);

  toggleShowPatientReply(): void {
    const id = this.session.selectedId();
    if (!id || this.togglingPatientReply()) return;
    const next = !this.showPatientReply();
    this._showPatientReplyOverride.set(next);
    this.togglingPatientReply.set(true);
    this.estabService.updateInfo(id, { show_patient_reply: next }).subscribe({
      next: () => {
        this.togglingPatientReply.set(false);
        this.toast.show('C\'est noté. Merci 👋', 'success', 4000, next ? 'Réponse patient activée' : 'Réponse patient désactivée');
      },
      error: () => {
        this._showPatientReplyOverride.set(!next);
        this.togglingPatientReply.set(false);
        this.toast.show('Erreur', 'error', 4000, 'Impossible de modifier le paramètre');
      },
    });
  }

  readonly togglingFeelingSection = signal(false);

  toggleShowFeelingSection(): void {
    const id = this.session.selectedId();
    if (!id || this.togglingFeelingSection()) return;
    const next = !this.showFeelingSection();
    this._showFeelingSectionOverride.set(next);
    this.togglingFeelingSection.set(true);
    this.estabService.updateInfo(id, { show_feeling_section: next }).subscribe({
      next: () => {
        this.togglingFeelingSection.set(false);
        this.toast.show('C\'est noté. Merci 👋', 'success', 4000, next ? 'Partage de ressenti activé' : 'Partage de ressenti désactivé');
      },
      error: () => {
        this._showFeelingSectionOverride.set(!next);
        this.togglingFeelingSection.set(false);
        this.toast.show('Erreur', 'error', 4000, 'Impossible de modifier le paramètre');
      },
    });
  }

  readonly togglingGoogleReview = signal(false);

  toggleShowGoogleReview(): void {
    const id = this.session.selectedId();
    if (!id || this.togglingGoogleReview() || !this.hasGooglePlace()) return;
    const next = !this.showGoogleReview();
    this._showGoogleReviewOverride.set(next);
    this.togglingGoogleReview.set(true);
    this.estabService.updateInfo(id, { show_google_review: next }).subscribe({
      next: () => {
        this.togglingGoogleReview.set(false);
        this.toast.show('C\'est noté. Merci 👋', 'success', 4000, next ? 'Avis Google activé' : 'Avis Google désactivé');
      },
      error: () => {
        this._showGoogleReviewOverride.set(!next);
        this.togglingGoogleReview.set(false);
        this.toast.show('Erreur', 'error', 4000, 'Impossible de modifier le paramètre');
      },
    });
  }

  readonly togglingUnderstandCare = signal(false);

  toggleShowUnderstandCare(): void {
    const id = this.session.selectedId();
    if (!id || this.togglingUnderstandCare()) return;
    const next = !this.showUnderstandCare();
    this._showUnderstandCareOverride.set(next);
    this.togglingUnderstandCare.set(true);
    this.estabService.updateInfo(id, { show_understand_care: next }).subscribe({
      next: () => {
        this.togglingUnderstandCare.set(false);
        this.toast.show('C\'est noté. Merci 👋', 'success', 4000, next ? 'Informations pratiques activées' : 'Informations pratiques désactivées');
      },
      error: () => {
        this._showUnderstandCareOverride.set(!next);
        this.togglingUnderstandCare.set(false);
        this.toast.show('Erreur', 'error', 4000, 'Impossible de modifier le paramètre');
      },
    });
  }

  readonly togglingQuiz = signal(false);

  toggleQuizEnabled(): void {
    const id = this.session.selectedId();
    if (!id || this.togglingQuiz()) return;
    const next = !this.quizEnabled();
    this._quizEnabledOverride.set(next);
    this.togglingQuiz.set(true);
    this.estabService.updateInfo(id, { quiz_enabled: next }).subscribe({
      next: () => {
        this.togglingQuiz.set(false);
        this.toast.show('C\'est noté. Merci 👋', 'success', 4000, next ? 'Quiz santé activé' : 'Quiz santé désactivé');
      },
      error: () => {
        this._quizEnabledOverride.set(!next);
        this.togglingQuiz.set(false);
        this.toast.show('Erreur', 'error', 4000, 'Impossible de modifier le paramètre');
      },
    });
  }

  readonly togglingPreventionPoint = signal(false);

  togglePreventionPointEnabled(): void {
    const id = this.session.selectedId();
    if (!id || this.togglingPreventionPoint()) return;
    const next = !this.preventionPointEnabled();
    this._preventionPointEnabledOverride.set(next);
    // Le backend remet aussi prevention_point_start_on_launch à FALSE côté serveur quand ce
    // toggle passe à OFF (n'a de sens que si le point prévention est actif) -- reflet local
    // immédiat pour ne pas laisser le toggle "au démarrage" grisé sur un état "on" obsolète.
    if (!next) this._preventionStartOnLaunchOverride.set(false);
    this.togglingPreventionPoint.set(true);
    this.estabService.updateInfo(id, { prevention_point_enabled: next }).subscribe({
      next: () => {
        this.togglingPreventionPoint.set(false);
        this.toast.show('C\'est noté. Merci 👋', 'success', 4000, next ? 'Point prévention activé' : 'Point prévention désactivé');
      },
      error: () => {
        this._preventionPointEnabledOverride.set(!next);
        this.togglingPreventionPoint.set(false);
        this.toast.show('Erreur', 'error', 4000, 'Impossible de modifier le paramètre');
      },
    });
  }

  /** Une ligne par langue active de l'établissement : widget.html/tv.html sont traduits (voir
   *  i18n-patient.js côté backend) et fixent leur langue une fois pour toutes via ?lang=,
   *  l'intégrateur choisissant l'URL correspondant à la langue voulue (pas de sélecteur en direct). */
  readonly widgetLanguageLinks = computed(() => {
    const linksWidget = this.estabDetails()?.links?.widget;
    if (!linksWidget) return [];
    const base = this.baseUrl.replace(/\/+$/, '');
    const absolute = linksWidget.startsWith('http') ? linksWidget : base + linksWidget;
    const langues = this.estabDetails()?.langues ?? ['fr'];
    return langues.map(code => ({
      code,
      label: PATIENT_LANGUAGES.find(l => l.code === code)?.label ?? code,
      url: absolute + (absolute.includes('?') ? '&' : '?') + 'lang=' + code,
    }));
  });

  readonly tvLanguageLinks = computed(() => {
    const linksWidget = this.estabDetails()?.links?.widget;
    if (!linksWidget) return [];
    const base = this.baseUrl.replace(/\/+$/, '');
    // Le slug établissement est déjà résolu par le backend dans links.widget (/public/widget/<slug>) —
    // pas de champ "slug" exposé séparément sur EstablishmentDetailsDto, donc on le réextrait ici
    // plutôt que d'ajouter un champ redondant côté API pour ce seul usage.
    const slugMatch = linksWidget.match(/\/public\/widget\/([^/?#]+)/);
    if (!slugMatch) return [];
    const tvBase = `${base}/public/establishments/${slugMatch[1]}/tv`;
    const langues = this.estabDetails()?.langues ?? ['fr'];
    return langues.map(code => ({
      code,
      label: PATIENT_LANGUAGES.find(l => l.code === code)?.label ?? code,
      url: `${tvBase}?lang=${code}`,
    }));
  });

  copyLanguageUrl(url: string, languageLabel: string): void {
    navigator.clipboard?.writeText(url).then(() => {
      this.toast.show(
        this.translate.instant('site.comm.kit.widget.linkCopiedTitle'),
        'success',
        4000,
        this.translate.instant('site.comm.kit.widget.linkCopiedSubtitle', { lang: languageLabel })
      );
    }).catch(() => {
      this.toast.show(
        this.translate.instant('site.comm.kit.widget.copyErrorTitle'),
        'error',
        4000,
        this.translate.instant('site.comm.kit.widget.copyErrorSubtitle')
      );
    });
  }

  /** Même lien org-scopé que "Widget Affluence Doctolib" du Kit de communication complet
   *  (qr-modal.component.ts: prescripteurUrl) -- réutilisé tel quel, pas une donnée établissement. */
  readonly doctolibWidgetUrl = computed(() => {
    const slug = this.orgDetails()?.slug;
    return slug ? `${this.baseUrl.replace(/\/+$/, '')}/public/organisations/${slug}/prescripteur` : null;
  });

  openDoctolibWidget(): void {
    const url = this.doctolibWidgetUrl();
    if (!url) return;
    window.open(url, '_blank');
  }

  readonly siteName = computed(() =>
    this.session.establishments().find(e => e.id === this.session.selectedId())?.name ?? 'Site'
  );
  private readonly _feedbacksTrigger = computed(() =>
    this.activeTab() === 'sat' ? this.session.selectedId() : null
  );

  readonly feedbacksData = toSignal(
    toObservable(this._feedbacksTrigger).pipe(
      switchMap(id => id
        ? this.satisfactionSvc.getFeedbacks(id).pipe(catchError(() => of(null)))
        : of(null)
      )
    ),
    { initialValue: null as FeedbackResponse | null }
  );

  // Deep-link minimal depuis "Pilotage multisites > Prévention" (Lot 7, clic sur une ligne de la
  // comparaison des sites) : ?tab=prevention ouvre directement l'onglet, jamais généralisé aux
  // autres onglets (aucun autre appelant n'en a besoin aujourd'hui). Le mode groupe (route
  // 'pilotage-prevention/sites/:id', voir groupContext ci-dessous) atterrit aussi directement sur
  // Prévention -- même règle, deux déclencheurs.
  readonly activeTab = signal<SiteTab>(
    this.route.snapshot.queryParamMap.get('tab') === 'prevention' || this.route.snapshot.data['groupContext']
      ? 'prevention' : 'comm'
  );

  /**
   * Drill-down Groupe -> Site (Ajustement UX/navigation, 2026-09-07) : cette instance de
   * SiteComponent est ouverte comme sous-page de pilotage Groupe (jamais une navigation
   * indépendante dans le module Site) -- masque le sélecteur de site (voir template), affiche un
   * breadcrumb "Prévention > Vue d'ensemble > {site}" + un retour explicite vers
   * `/pilotage-prevention`. Discriminé par `route.data.groupContext` (jamais par la simple présence
   * d'un `establishmentId` en paramètre, pour rester robuste à toute évolution future de la route
   * standalone). Ne duplique aucune logique métier : réutilise {@link SessionService#selectEstablishment}
   * tel quel (même mécanisme que l'ancien `openSite()` de Vue d'ensemble), aucun nouvel appel API.
   */
  readonly groupContext = signal(!!this.route.snapshot.data['groupContext']);

  private readonly groupContextEstablishmentId = toSignal(
    this.route.paramMap.pipe(map(p => p.get('establishmentId'))),
    { initialValue: this.route.snapshot.paramMap.get('establishmentId') }
  );

  readonly groupContextSiteName = computed(() =>
    this.session.establishments().find(e => e.id === this.session.selectedId())?.name ?? ''
  );

  /** Retour Groupe (jamais vers la route Site standalone) : préserve la période sélectionnée sur
   *  la Vue d'ensemble si elle a été transmise à l'entrée (`?days=`), jamais la position de scroll
   *  (complexité non demandée pour un gain marginal). */
  backToGroupOverview(): void {
    const days = this.route.snapshot.queryParamMap.get('days');
    this.router.navigate(['/pilotage-prevention'], days ? { queryParams: { days } } : {});
  }

  /** Filtre de période du bloc "Engagement patients" (Indicateurs qualité) — 'all' = depuis le début. */
  readonly qualityPeriod = signal<QualityStatsPeriod>('all');

  private readonly _qualityTrigger = computed(() =>
    this.activeTab() === 'sat' ? this.session.selectedId() : null
  );

  private readonly _qualityStatsTrigger = computed(() =>
    this.activeTab() === 'sat' ? { id: this.session.selectedId(), period: this.qualityPeriod() } : null
  );

  readonly statsData = toSignal(
    toObservable(this._qualityStatsTrigger).pipe(
      switchMap(trigger => trigger?.id
        ? this.satisfactionSvc.getEstablishmentStats(trigger.id, trigger.period).pipe(catchError(() => of(null)))
        : of(null)
      )
    ),
    { initialValue: null as EstablishmentStats | null }
  );

  readonly avgScore          = computed(() => this.statsData()?.avg_score ?? null);
  readonly totalFeedback     = computed(() => this.statsData()?.total_feedbacks ?? null);
  readonly totalQr           = computed(() => this.statsData()?.total_qr ?? null);
  readonly participationRate = computed(() => this.statsData()?.participation_rate ?? null);
  readonly googleReviewClicks = computed(() => this.statsData()?.google_review_clicks ?? null);
  private formatFrDate(d: Date): string {
    return d.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' });
  }

  readonly firstFeedbackDate = computed(() => {
    const raw = this.statsData()?.first_feedback_date;
    if (!raw) return null;
    return this.formatFrDate(new Date(raw));
  });

  /** Libellé de la plage de dates affichée à côté du titre "Engagement patients", selon le filtre actif. */
  readonly qualityPeriodRangeLabel = computed(() => {
    const period = this.qualityPeriod();
    const today = this.formatFrDate(new Date());

    if (period === 'all') {
      const first = this.firstFeedbackDate();
      return first
        ? this.translate.instant('site.sat.quality.rangeSince', { date: first })
        : this.translate.instant('site.sat.quality.rangeSinceUnknown');
    }

    const days = period === '7d' ? 7 : 30;
    const from = new Date();
    from.setDate(from.getDate() - days);
    return this.translate.instant('site.sat.quality.rangeBetween', { from: this.formatFrDate(from), to: today });
  });

  readonly monthlyHistoryData = toSignal(
    toObservable(this._qualityTrigger).pipe(
      switchMap(id => id
        ? this.satisfactionSvc.getMonthlyHistory(id).pipe(catchError(() => of(null)))
        : of(null)
      )
    ),
    { initialValue: null as MonthlySatisfactionPoint[] | null }
  );

  private static readonly CHART_X = [118, 191, 265, 338, 412, 485];

  readonly satisfactionChartLabels = computed(() =>
    (this.monthlyHistoryData() ?? []).map(p =>
      this.translate.instant('common.months.shortByIndex.' + (Number(p.month.slice(5, 7)) - 1))
    )
  );

  readonly satisfactionChartPoints = computed(() => {
    const data = this.monthlyHistoryData() ?? [];
    return data.map((p, i) => ({
      x: SiteComponent.CHART_X[i] ?? SiteComponent.CHART_X[SiteComponent.CHART_X.length - 1],
      y: p.avg_score != null ? 170 - (p.avg_score / 5) * 160 : null,
      pct: p.avg_score != null ? Math.round((p.avg_score / 5) * 100) : null,
    }));
  });

  readonly satisfactionChartPolyline = computed(() =>
    this.satisfactionChartPoints()
      .filter(p => p.y != null)
      .map(p => `${p.x},${p.y}`)
      .join(' ')
  );

  readonly hasSatisfactionHistory = computed(() =>
    this.satisfactionChartPoints().some(p => p.y != null)
  );

  /** Notes par statut d'attente (Fluide / Modéré / Forte affluence), calculées depuis les avis du site courant */
  readonly waitStatusStats = computed(() => {
    const data = this.feedbacksData();
    if (!data) return null;

    const buckets: Record<1 | 2 | 3, number[]> = { 1: [], 2: [], 3: [] };
    for (const f of data.feedbacks) {
      if (f.reported_bucket === 1 || f.reported_bucket === 2 || f.reported_bucket === 3) {
        buckets[f.reported_bucket].push(f.score ?? NaN);
      }
    }

    const avg = (scores: number[]) => {
      const valid = scores.filter(s => !isNaN(s));
      return valid.length ? valid.reduce((a, b) => a + b, 0) / valid.length : null;
    };

    const total = buckets[1].length + buckets[2].length + buckets[3].length;
    const pct = (count: number) => (total > 0 ? Math.round((count / total) * 100) : 0);

    const fluideAvg = avg(buckets[1]);
    const critiqueAvg = avg(buckets[3]);

    return {
      total,
      fluide:   { count: buckets[1].length, avg: fluideAvg,        pct: pct(buckets[1].length) },
      tension:  { count: buckets[2].length, avg: avg(buckets[2]),  pct: pct(buckets[2].length) },
      critique: { count: buckets[3].length, avg: critiqueAvg,      pct: pct(buckets[3].length) },
      delta: (fluideAvg != null && critiqueAvg != null) ? fluideAvg - critiqueAvg : null,
    };
  });

  readonly scoreDistribution = computed((): number[] | null => {
    const s = this.statsData();
    if (!s) return null;
    return [s.score_1_pct, s.score_2_pct, s.score_3_pct, s.score_4_pct, s.score_5_pct];
  });
  readonly avisFilter = signal<number>(0);

  readonly filteredAvis = computed(() => {
    const data = this.feedbacksData();
    if (!data) return [];
    const f = this.avisFilter();
    return f === 0 ? data.feedbacks : data.feedbacks.filter(a => a.score === f);
  });
  readonly pendingReplyCount = computed(() => {
    const data = this.feedbacksData();
    if (!data) return 0;
    return data.feedbacks.filter(f => f.wants_reply && f.contact_email && !f.replied_at).length;
  });
  readonly showAccueilSubpage = signal(false);
  readonly showKitSubpage = signal(false);
  readonly showActuSubpage = signal(false);
  readonly showPrevisionSubpage = signal(false);
  readonly showSatisfactionSubpage = signal(false);
  readonly showQualiteSubpage = signal(false); // DEPRECATED (2026-09-11) : voir la note en tête de hasQualitePermission ci-dessus
  readonly showParamsSubpage = signal(false);
  readonly showProfileIaSubpage = signal(false);
  readonly showEquipementsSubpage = signal(false);
  readonly showGestionEquipesSubpage = signal(false);
  readonly gestionEquipesStartInCreate = signal(false);

  // Edition inline des messages patients
  readonly editingQueueId = signal<string | null>(null);
  readonly editingMessage = signal<string>('');
  readonly isSavingMessage = signal<boolean>(false);

  // Projection graph
  readonly selectedGraphDay = signal<number>(this.timezone.getDateInEstablishmentTimezone().getDay());
  readonly selectedGraphQueueId = signal<string | null>(null);
  readonly graphQueueId = computed(() => this.selectedGraphQueueId() || (this.session.queues().length > 0 ? this.session.queues()[0].id : undefined));

  /** value = JS Date.getDay() (0=dimanche) ; labelKey pointe vers common.days.shortByCode, résolu via | translate au template. */
  days = [
    { value: 1, labelKey: 'common.days.shortByCode.mon' },
    { value: 2, labelKey: 'common.days.shortByCode.tue' },
    { value: 3, labelKey: 'common.days.shortByCode.wed' },
    { value: 4, labelKey: 'common.days.shortByCode.thu' },
    { value: 5, labelKey: 'common.days.shortByCode.fri' },
    { value: 6, labelKey: 'common.days.shortByCode.sat' },
    { value: 0, labelKey: 'common.days.shortByCode.sun' }
  ];

  selectGraphDay(day: number) { this.selectedGraphDay.set(day); }
  onGraphQueueChanged(id: string) { this.selectedGraphQueueId.set(id); }

  openAccueil() { this.showAccueilSubpage.set(true); }
  closeAccueil() { this.showAccueilSubpage.set(false); }

  openKit() { this.showKitSubpage.set(true); }
  closeKit() { this.showKitSubpage.set(false); }

  openActu() { this.showActuSubpage.set(true); }
  closeActu() { this.showActuSubpage.set(false); }

  openPrevision() { this.showPrevisionSubpage.set(true); }
  closePrevision() { this.showPrevisionSubpage.set(false); }

  readonly selectedFeedbackId = signal<string | null>(null);

  openSatisfaction(feedbackId?: string) {
    this.selectedFeedbackId.set(feedbackId ?? null);
    this.showSatisfactionSubpage.set(true);
  }
  closeSatisfaction() { this.showSatisfactionSubpage.set(false); }

  // DEPRECATED (2026-09-11) : plus aucun appelant (bouton "Voir plus" retiré) -- voir la note en
  // tête de hasQualitePermission ci-dessus.
  openQualite() { this.showQualiteSubpage.set(true); }
  closeQualite() { this.showQualiteSubpage.set(false); }

  openParams() { this.showParamsSubpage.set(true); }
  closeParams() { this.showParamsSubpage.set(false); }

  openProfileIa() { this.showProfileIaSubpage.set(true); }
  closeProfileIa() { this.showProfileIaSubpage.set(false); }

  openEquipements() { this.showEquipementsSubpage.set(true); }
  closeEquipements() { this.showEquipementsSubpage.set(false); }

  openGestionEquipes(startInCreate = false) {
    this.gestionEquipesStartInCreate.set(startInCreate);
    this.showGestionEquipesSubpage.set(true);
  }
  closeGestionEquipes() {
    this.showGestionEquipesSubpage.set(false);
    this.refreshAllUsers();
  }

  formatActuDate(dateStr: string): string {
    const date = new Date(dateStr);
    const diffDays = Math.floor((Date.now() - date.getTime()) / 86_400_000);
    if (diffDays === 0) return this.translate.instant('site.common.today');
    if (diffDays === 1) return this.translate.instant('site.common.yesterday');
    if (diffDays < 7)  return this.translate.instant('site.common.daysAgo', { days: diffDays });
    const month = this.translate.instant('common.months.shortByIndex.' + date.getMonth());
    return `${date.getDate()} ${month}`;
  }

  /** Statut Fluide/Modéré/Forte affluence en direct (pas juste ouvert/fermé), seuils configurés dans Qualité·Objectifs. */
  queueAffluenceColor(q: QueueSnapshot): string {
    // "level" et "open" viennent directement de GET /api/v1/queues (déjà chargé dans session.queues()) —
    // pas d'appel réseau supplémentaire. level : 20/50=Fluide/Modéré, 80/100=Forte affluence (seuils configurés
    // dans Qualité·Objectifs, résolus côté backend).
    if (q.status === 'CLOSED' || q.open === false) return 'var(--mu)';
    if (q.level == null) return 'var(--mu)';
    if (q.level <= 20) return 'var(--gn)';
    if (q.level <= 50) return 'var(--or)';
    return 'var(--rd)';
  }

  startEditMessage(q: QueueSnapshot): void {
    this.editingQueueId.set(q.id);
    this.editingMessage.set(q.patient_message ?? '');
  }

  cancelEditMessage(): void {
    this.editingQueueId.set(null);
    this.editingMessage.set('');
  }

  formatScore(score: number | null): string {
    if (!score) return '—';
    return score.toFixed(1);
  }

  formatPct(value: number | null): string {
    if (value === null || value === undefined) return '—';
    return Math.round(value) + '%';
  }

  roundScore(score: number | null): number {
    return score !== null ? Math.round(score) : 0;
  }

  scoreBarPct(star: number): number {
    return this.scoreDistribution()?.[star - 1] ?? 0;
  }

  setQualityPeriod(period: QualityStatsPeriod): void {
    this.qualityPeriod.set(period);
  }

  starsArray(score: number | null): boolean[] {
    const s = score ?? 0;
    return Array.from({ length: 5 }, (_, i) => i < s);
  }

  bucketBadge(bucket: number | null): { label: string; cls: string } | null {
    const map: Record<number, { key: string; cls: string }> = {
      1: { key: 'fast', cls: 'g' },
      2: { key: 'moderate', cls: 'o' },
      3: { key: 'long', cls: 'r' },
    };
    const entry = bucket ? map[bucket] : null;
    return entry ? { label: this.translate.instant('site.sat.reviews.bucket.' + entry.key), cls: entry.cls } : null;
  }

  formatAvisDate(dateStr: string): string {
    const date = new Date(dateStr);
    return date.toLocaleString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  }

  saveMessage(queueId: string): void {
    if (this.isSavingMessage()) return;
    this.isSavingMessage.set(true);
    const msg = this.editingMessage().trim() || null;
    this.estabService.updateQueue(queueId, { patient_message: msg }).subscribe({
      next: () => {
        this.session.queues.update(qs =>
          qs.map(q => q.id === queueId ? { ...q, patient_message: msg } : q)
        );
        this.editingQueueId.set(null);
        this.editingMessage.set('');
        this.isSavingMessage.set(false);
        this.toast.show('C\'est noté. Merci 👋', 'success', 4000, 'Message patient mis à jour');
      },
      error: () => {
        this.isSavingMessage.set(false);
        this.toast.show('Erreur', 'error', 4000, 'Impossible de sauvegarder le message');
      },
    });
  }

  // ═══════════════════════════════════ PRÉVENTION ═══════════════════════════════════
  // MVP INTEREST_ONLY uniquement : pas de À confirmer/Réalisé/Non réalisé, pas de QR/code staff
  // (STAFF_CONFIRMATION reste dans le backend mais n'est exposé nulle part ici -- décision produit
  // du 2026-08-26, PREVENTION_SPEC.md section 10 Lot 5).

  private readonly _preventionHomeTrigger = computed(() =>
    this.activeTab() === 'prevention' ? this.session.selectedId() : null
  );

  readonly preventionPerformance = toSignal(
    toObservable(this._preventionHomeTrigger).pipe(
      switchMap(id => id ? this.preventionSvc.getPerformance(id).pipe(catchError(() => of(null))) : of(null))
    ),
    { initialValue: null as PreventionPerformance | null }
  );

  readonly preventionFunnel = toSignal(
    toObservable(this._preventionHomeTrigger).pipe(
      switchMap(id => id ? this.preventionSvc.getFunnel(id).pipe(catchError(() => of(null))) : of(null))
    ),
    { initialValue: null as PreventionFunnel | null }
  );

  /** Aperçu home (3 dernières lignes) -- la sous-page Activité recharge sa propre liste paginée séparément. */
  readonly preventionActivityPreview = toSignal(
    toObservable(this._preventionHomeTrigger).pipe(
      switchMap(id => id ? this.preventionSvc.getActivity(id, 0, 3).pipe(catchError(() => of(null))) : of(null))
    ),
    { initialValue: null as ActivityPage | null }
  );

  private readonly _preventionCatalogRefresh = signal(0);
  private readonly _preventionCatalogTrigger = computed(() => ({
    id: this.activeTab() === 'prevention' ? this.session.selectedId() : null,
    refresh: this._preventionCatalogRefresh(),
  }));

  readonly preventionCatalog = toSignal(
    toObservable(this._preventionCatalogTrigger).pipe(
      switchMap(({ id }) => id ? this.preventionSvc.getEstablishmentCatalog(id).pipe(catchError(() => of(null))) : of(null))
    ),
    { initialValue: null as EstablishmentOfferCatalog[] | null }
  );

  /** Offres actives sur ce site -- jamais confondu avec "0 OrganizationOffer" (empty state réel, section 24 du lot E.E.G) : un catalogue non vide avec 0 offre active reste affiché comme liste d'offres inactives. */
  readonly activeOffers = computed(() => (this.preventionCatalog() ?? []).filter(o => o.activeForEstablishment));
  readonly activeOffersCount = computed(() => this.activeOffers().length);
  /** Offres affichées dans "Offres du site" : les actives, plus -- le temps de sa saisie -- l'offre en
   * cours d'activation depuis le catalogue du groupe (`onActivateOfferForCurrentSite`), qui n'est pas
   * encore active tant que `saveOffer()` n'a pas réussi. Sans ce cas, la carte d'édition n'aurait tout
   * simplement aucune ligne où s'afficher (la liste n'itère que les offres déjà actives). */
  readonly offersToShowInSiteList = computed(() => {
    const active = this.activeOffers();
    const editingId = this.editingOfferId();
    if (!editingId || active.some(o => o.offerId === editingId)) return active;
    const editingOffer = (this.preventionCatalog() ?? []).find(o => o.offerId === editingId);
    return editingOffer ? [...active, editingOffer] : active;
  });

  // ── Interlocuteur patient (Lot 7, Site > Prévention > Configuration) -- même trigger que le
  // catalogue (chargé dès que l'onglet Prévention est actif), jamais couplé à conversion_tracking_mode
  // (reste hors UI, décision de re-cadrage Lot 5). ──
  private readonly _contactConfigRefresh = signal(0);
  private readonly _contactConfigTrigger = computed(() => ({
    id: this.activeTab() === 'prevention' ? this.session.selectedId() : null,
    refresh: this._contactConfigRefresh(),
  }));

  readonly contactConfig = toSignal(
    toObservable(this._contactConfigTrigger).pipe(
      switchMap(({ id }) => id ? this.preventionSvc.getContactConfig(id).pipe(catchError(() => of(null))) : of(null))
    ),
    { initialValue: null as EstablishmentContact | null }
  );

  readonly contactRoleDraft = signal<PatientContactRole | null>(null);
  readonly savingContactRole = signal(false);
  /** « Après le prélèvement, le patient repasse par l'accueil » (V330, case unique par site). */
  readonly returnToReceptionDraft = signal(false);
  /** Identité du patient avec la demande, point fait sur place (V331, maquette S01). */
  readonly identityModeDraft = signal<PatientIdentityMode>('NONE');
  /** Attestation à cocher la première fois qu'on active l'identité (le serveur refuse sans elle). */
  readonly identityAttestationDraft = signal(false);
  readonly identityAttestationNeeded = computed(() => this.identityModeDraft() !== 'NONE' && !this.contactConfig()?.identityAttested);
  readonly contactSaveBlocked = computed(() => this.identityAttestationNeeded() && !this.identityAttestationDraft());
  readonly contactRoleDirty = computed(() =>
    this.contactRoleDraft() !== (this.contactConfig()?.patientContactRole ?? null)
    || this.returnToReceptionDraft() !== !!this.contactConfig()?.patientReturnToReception
    || this.identityModeDraft() !== (this.contactConfig()?.patientIdentityOnSite ?? 'NONE'));

  onIdentityModeChanged(value: string): void {
    this.identityModeDraft.set((value === 'OPTIONAL' || value === 'REQUIRED') ? value : 'NONE');
  }

  /** « MARTIN Claire · 12/03/1968 » -- identité déclarée par le patient, ou null. */
  patientIdentityLabel(item: ActivityItem): string | null {
    if (!item.patientLastName || !item.patientFirstName) return null;
    const birth = item.patientBirthDate ? item.patientBirthDate.split('-').reverse().join('/') : '';
    return [`${item.patientLastName.toUpperCase()} ${item.patientFirstName}`, birth].filter(Boolean).join(' · ');
  }
  /**
   * La case n'a de sens que si le préleveur est l'interlocuteur d'au moins un bilan : défaut du site
   * (brouillon en cours) ou override d'une offre active. Masquée sinon -- la valeur stockée est
   * conservée telle quelle (jamais décochée en silence).
   */
  readonly showReturnToReception = computed(() =>
    this.contactRoleDraft() === 'PRELEVEUR'
    || this.activeOffers().some(o => o.patientContactRole === 'PRELEVEUR'));

  onReturnToReceptionChanged(checked: boolean): void {
    this.returnToReceptionDraft.set(checked);
  }

  onContactRoleChanged(value: string): void {
    this.contactRoleDraft.set((value === '' ? null : value) as PatientContactRole | null);
  }

  private static readonly CONTACT_ROLE_LABEL_KEYS: Record<PatientContactRole, string> = {
    ACCUEIL: 'site.prevention.contact.roles.ACCUEIL',
    PRELEVEUR: 'site.prevention.contact.roles.PRELEVEUR',
    BIOLOGISTE: 'site.prevention.contact.roles.BIOLOGISTE',
    AUTRE: 'site.prevention.contact.roles.AUTRE',
  };

  /** Libellé traduit d'un rôle, ou "Non configuré" -- utilisé pour afficher le défaut du site résolu quand l'override par offre vaut `null` (section 20 du lot E.E.G). */
  contactRoleLabel(role: PatientContactRole | null): string {
    if (!role) return this.translate.instant('site.prevention.contact.notConfigured');
    return this.translate.instant(SiteComponent.CONTACT_ROLE_LABEL_KEYS[role]);
  }

  /**
   * Libellé "défaut du site" pour une offre sans override (Lot E.J.1 -- corrige la divergence de
   * wording identifiée) : `patientContactRole = null` signifie "suit le défaut du site", jamais
   * "Non configuré" -- cette valeur reste réservée au champ de configuration SITE lui-même
   * (`contactRoleLabel`), jamais à l'affichage d'une offre. Deux cas :
   * - le site a lui-même une valeur -- "Défaut du site : {role}" (contactRoleDefaultDisplay) ;
   * - le site n'a lui-même jamais rien configuré -- "Défaut du site" seul (contactRoleDefaultOption,
   *   déjà utilisé comme option vide du select), jamais "Défaut du site : Non configuré".
   * Purement un choix d'affichage : la règle de fallback backend (EstablishmentOfferAvailability
   * override -> EstablishmentConfig -> null) n'est pas modifiée.
   */
  siteDefaultContactRoleDisplay(): string {
    const siteRole = this.contactConfig()?.patientContactRole ?? null;
    return siteRole
      ? this.translate.instant('site.prevention.offer.contactRoleDefaultDisplay', { role: this.contactRoleLabel(siteRole) })
      : this.translate.instant('site.prevention.offer.contactRoleDefaultOption');
  }

  saveContactRole(): void {
    const establishmentId = this.session.selectedId();
    if (!establishmentId || this.savingContactRole() || this.contactSaveBlocked()) return;

    this.savingContactRole.set(true);
    this.preventionSvc.setContactConfig(establishmentId, this.contactRoleDraft(), this.returnToReceptionDraft(), this.identityModeDraft(),
      this.identityAttestationNeeded() && this.identityAttestationDraft()).subscribe({
      next: () => {
        this.savingContactRole.set(false);
        this._contactConfigRefresh.update(n => n + 1);
        this.toast.show('C\'est noté. Merci 👋', 'success', 4000, 'Interlocuteur mis à jour');
      },
      error: (err) => {
        this.savingContactRole.set(false);
        this.toast.show('Erreur', 'error', 4000, err?.error?.message || 'Impossible de mettre à jour l\'interlocuteur');
      },
    });
  }

  // Dénominateur = recommendationsReachedDecision (DISPLAYED ou décision), jamais recommendationsDisplayed
  // seul (best-effort frontend, peut sous-compter et faire dépasser 100% -- fiabilisé le 2026-08-26).
  // Jamais de cap artificiel à 100% : un taux élevé signale un vrai problème de tracking à investiguer.
  readonly preventionInterestRate = computed(() => {
    const funnel = this.preventionFunnel();
    if (!funnel || funnel.recommendationsReachedDecision === 0) return null;
    return Math.round((funnel.interested / funnel.recommendationsReachedDecision) * 100);
  });

  // ── Analytics (Lot 6) : Parcours patient / Performance & impact -- sélecteur de période commun
  // 7/30/90j (défaut 30), jamais recalculé depuis des événements bruts côté Angular : le backend
  // renvoie déjà completionRate/interestRate calculés (FunnelAnalytics/PerformanceAnalytics). ──
  readonly preventionPeriodDays = signal(30);

  private readonly _preventionAnalyticsTrigger = computed(() => ({
    id: this.activeTab() === 'prevention' ? this.session.selectedId() : null,
    days: this.preventionPeriodDays(),
  }));

  readonly preventionFunnelAnalytics = toSignal(
    toObservable(this._preventionAnalyticsTrigger).pipe(
      switchMap(({ id, days }) => id ? this.preventionSvc.getFunnelAnalytics(id, days).pipe(catchError(() => of(null))) : of(null))
    ),
    { initialValue: null as FunnelAnalytics | null }
  );

  readonly preventionPerformanceAnalytics = toSignal(
    toObservable(this._preventionAnalyticsTrigger).pipe(
      switchMap(({ id, days }) => id ? this.preventionSvc.getPerformanceAnalytics(id, days).pipe(catchError(() => of(null))) : of(null))
    ),
    { initialValue: null as PerformanceAnalytics | null }
  );

  /** Résumé Coverage compact pour la carte "Couverture & offres" (Lot V2A.5) -- même trigger
   *  (id + preventionPeriodDays) que le funnel/performance, jamais un sélecteur de période dédié. */
  readonly siteCoverageSummary = toSignal(
    toObservable(this._preventionAnalyticsTrigger).pipe(
      switchMap(({ id, days }) => id ? this.preventionSvc.getNeedsCoverageSummary(id, days).pipe(catchError(() => of(null))) : of(null))
    ),
    { initialValue: null as SiteNeedCoverageSummaryView | null }
  );

  private static readonly COVERAGE_GAP_REASON_KEYS: Record<CoverageState, string> = {
    NO_BILAN_MAPPING: 'besoinsCouverture.gapReason.NO_BILAN_MAPPING',
    BILAN_AVAILABLE_NO_OFFER: 'besoinsCouverture.gapReason.BILAN_AVAILABLE_NO_OFFER',
    OFFER_UNAVAILABLE_ON_SITE: 'besoinsCouverture.gapReason.OFFER_UNAVAILABLE_ON_SITE',
    UNCLASSIFIED: 'besoinsCouverture.gapReason.UNCLASSIFIED',
    NO_OFFER_CONFIGURED_ON_SITE: 'besoinsCouverture.gapReason.NO_OFFER_CONFIGURED_ON_SITE',
  };

  /** Réutilise le wording déjà traduit (5 locales) du dashboard Groupe -- une seule source de vérité pour ces libellés, jamais une 2e traduction divergente. */
  siteCoverageGapReasonLabel(reason: CoverageState | null): string {
    if (!reason) return '—';
    return this.translate.instant(SiteComponent.COVERAGE_GAP_REASON_KEYS[reason]);
  }

  /** Tooltip explicatif -- UNIQUEMENT pour UNCLASSIFIED (Lot V2A.5.1, §3), jamais prétendre identifier la porte exacte d'OfferResolver. */
  siteCoverageGapReasonTooltip(reason: CoverageState | null): string | null {
    if (reason !== 'UNCLASSIFIED') return null;
    return this.translate.instant('besoinsCouverture.gapReason.UNCLASSIFIED_TOOLTIP');
  }

  private static readonly COVERAGE_CTA_KEYS: Record<CoverageState, string> = {
    OFFER_UNAVAILABLE_ON_SITE: 'site.prevention.offer.coverage.cta.manageOffers',
    NO_BILAN_MAPPING: 'site.prevention.offer.coverage.cta.viewLibrary',
    BILAN_AVAILABLE_NO_OFFER: 'site.prevention.offer.coverage.cta.manageOffers',
    UNCLASSIFIED: 'site.prevention.offer.coverage.cta.viewConfig',
    NO_OFFER_CONFIGURED_ON_SITE: 'site.prevention.offer.coverage.cta.configureOffers',
  };

  siteCoverageCtaLabel(reason: CoverageState | null): string {
    if (!reason) return '';
    return this.translate.instant(SiteComponent.COVERAGE_CTA_KEYS[reason]);
  }

  /** Data -> action (§8 du lot V2A.5) : chaque frein Coverage principal mène directement à l'écran
   *  le plus susceptible de le résoudre -- jamais une nouvelle vue Coverage dédiée côté Site. */
  onSiteCoverageCta(reason: CoverageState | null): void {
    if (reason === 'NO_BILAN_MAPPING') {
      this.router.navigate(['/prevention-offres/bibliotheque']);
      return;
    }
    // OFFER_UNAVAILABLE_ON_SITE / BILAN_AVAILABLE_NO_OFFER / UNCLASSIFIED / NO_OFFER_CONFIGURED_ON_SITE -> configuration des offres de CE site.
    this.openPreventionCatalog();
  }

  /** Podium (top 3) du bloc home Performance & impact -- offers déjà triées par valeur décroissante côté backend. */
  readonly preventionTopOffers = computed(() => (this.preventionPerformanceAnalytics()?.offers ?? []).slice(0, 3));

  readonly podiumColors = ['#F0B429', '#A0A4AE', '#C97A3D'];

  setPreventionPeriodDays(days: number): void {
    this.preventionPeriodDays.set(days);
  }

  formatPreventionRate(rate: number | null | undefined): string {
    if (rate == null) return '—';
    return Math.round(rate * 100) + '%';
  }

  readonly outcomeKindLabelKeys: Record<string, string> = {
    RECOMMENDATION: 'site.prevention.journey.outcomes.recommendation',
    INSUFFICIENT_INFORMATION: 'site.prevention.journey.outcomes.insufficientInformation',
    ALREADY_FOLLOWED: 'site.prevention.journey.outcomes.alreadyFollowed',
    CLINICAL_REVIEW_REQUIRED: 'site.prevention.journey.outcomes.clinicalReviewRequired',
    INDICATED_WITHOUT_OFFER: 'site.prevention.journey.outcomes.indicatedWithoutOffer',
    NO_RECOMMENDATION: 'site.prevention.journey.outcomes.noRecommendation',
  };

  /** Harmonisation esthétique avec "Notes par statut d'attente" (Fidélisation patient) -- mêmes
   * teintes de fond/bordure déjà établies dans ce fichier pour vert/orange, +violet de marque pour
   * "déjà suivi" (informatif, ni positif ni négatif) et neutre gris pour "aucune recommandation"
   * (le patient n'était simplement pas éligible, pas un signal d'alerte).
   * CLINICAL_REVIEW_REQUIRED (Wave 1 Cœliaque) réutilise la teinte orange (même registre
   * "nécessite une action/attention" que INSUFFICIENT_INFORMATION) ; INDICATED_WITHOUT_OFFER
   * réutilise la teinte violette (informatif, comme ALREADY_FOLLOWED). */
  readonly outcomeKindTint: Record<string, { bg: string; border: string; color: string }> = {
    RECOMMENDATION: { bg: '#f0fdf4', border: '1px solid rgba(21,151,107,.2)', color: 'var(--gn)' },
    INSUFFICIENT_INFORMATION: { bg: '#fffbeb', border: '1px solid rgba(194,115,0,.2)', color: 'var(--or)' },
    ALREADY_FOLLOWED: { bg: 'var(--ps)', border: '1px solid rgba(81,62,155,.2)', color: 'var(--p)' },
    CLINICAL_REVIEW_REQUIRED: { bg: '#fffbeb', border: '1px solid rgba(194,115,0,.2)', color: 'var(--or)' },
    INDICATED_WITHOUT_OFFER: { bg: 'var(--ps)', border: '1px solid rgba(81,62,155,.2)', color: 'var(--p)' },
    NO_RECOMMENDATION: { bg: 'var(--bg)', border: '1px solid var(--bd)', color: 'var(--mu)' },
  };

  /** Le backend retient les totaux du funnel (started/completed/taux/résultats/évolution) : jamais affichés comme 0. */
  readonly preventionFunnelWithheld = computed(() => this.preventionFunnelAnalytics()?.insufficientData === true);

  /** Refonte résultat (2026-10-16) -- « Où les patients disent oui » : intérêts par écran, ordre fixe, « non précisé » seulement s'il existe. */
  readonly preventionDecisionOrigins = computed(() => {
    const byOrigin = this.preventionFunnelAnalytics()?.interestedByOrigin ?? {};
    const rows = ['RESULT', 'OFFER_DETAIL', 'HOME_CARD'].map(key => ({ key, count: byOrigin[key] ?? 0 }));
    if ((byOrigin['UNKNOWN'] ?? 0) > 0) rows.push({ key: 'UNKNOWN', count: byOrigin['UNKNOWN'] ?? 0 });
    return rows;
  });

  readonly preventionOutcomesTotal = computed(() =>
    (this.preventionFunnelAnalytics()?.outcomes ?? []).reduce((sum, o) => sum + o.count, 0));

  outcomePct(count: number): number {
    const total = this.preventionOutcomesTotal();
    return total > 0 ? Math.round((count / total) * 100) : 0;
  }

  // ── Mini timeline SVG (pas de librairie de graphique, même esprit que forecast-graph) --
  // enrichie (grille, axe, point + valeur du dernier jour) pour la même lisibilité que le
  // graphique "Taux de satisfaction" de Fidélisation patient, harmonisation esthétique 2026-08-29.
  private buildSparklineData(values: number[], width: number, height: number, max: number, padding = 4):
      { pointsAttr: string; last: { x: number; y: number; value: number } | null } {
    if (values.length === 0) return { pointsAttr: '', last: null };
    const usableMax = Math.max(max, 1);
    const stepX = values.length > 1 ? (width - padding * 2) / (values.length - 1) : 0;
    const coords = values.map((v, i) => {
      const x = padding + i * stepX;
      const y = height - padding - (v / usableMax) * (height - padding * 2);
      return { x, y, value: v };
    });
    return { pointsAttr: coords.map(c => `${c.x.toFixed(1)},${c.y.toFixed(1)}`).join(' '), last: coords[coords.length - 1] };
  }

  readonly offerDetailTimelineMax = computed(() => {
    const points = this.preventionOfferDetail()?.timeline ?? [];
    return Math.max(...points.map(p => p.opportunities), 1);
  });

  readonly offerDetailTimelineData = computed(() => {
    const points = this.preventionOfferDetail()?.timeline ?? [];
    return this.buildSparklineData(points.map(p => p.opportunities), 280, 70, this.offerDetailTimelineMax());
  });

  readonly offerDetailTimelinePoints = computed(() => this.offerDetailTimelineData().pointsAttr);

  formatPreventionDate(dateStr: string): string {
    const date = new Date(dateStr);
    const month = this.translate.instant('common.months.shortByIndex.' + date.getMonth());
    const time = date.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
    return `${date.getDate()} ${month} · ${time}`;
  }

  formatPreventionPrice(price: number | null, currency: string | null): string {
    if (price == null) return '—';
    return `${price % 1 === 0 ? price : price.toFixed(2)} ${currency === 'EUR' ? '€' : (currency ?? '')}`;
  }

  // ── Sous-pages ──
  readonly showPreventionActivitySubpage = signal(false);
  readonly showPreventionCatalogSubpage = signal(false);
  readonly showPreventionJourneySubpage = signal(false);
  readonly showPreventionPerformanceSubpage = signal(false);

  openPreventionJourney(): void { this.showPreventionJourneySubpage.set(true); }
  closePreventionJourney(): void { this.showPreventionJourneySubpage.set(false); }

  openPreventionPerformance(): void {
    this.showPreventionPerformanceSubpage.set(true);
    this.preventionOfferDetail.set(null);
  }
  closePreventionPerformance(): void {
    this.showPreventionPerformanceSubpage.set(false);
    this.preventionOfferDetail.set(null);
  }

  // ── Drill-down "Performance par bilan" (3e niveau, sous Performance & impact) ──
  readonly preventionOfferDetail = signal<OfferPerformanceDetail | null>(null);
  readonly preventionOfferDetailLoading = signal(false);

  openOfferDetail(offerId: string): void {
    const establishmentId = this.session.selectedId();
    if (!establishmentId) return;
    this.preventionOfferDetailLoading.set(true);
    this.preventionSvc.getOfferPerformanceDetail(establishmentId, offerId, this.preventionPeriodDays()).subscribe({
      next: (detail) => {
        this.preventionOfferDetail.set(detail);
        this.preventionOfferDetailLoading.set(false);
      },
      error: () => {
        this.preventionOfferDetailLoading.set(false);
        this.toast.show('Erreur', 'error', 4000, 'Impossible de charger le détail du bilan');
      },
    });
  }
  closeOfferDetail(): void { this.preventionOfferDetail.set(null); }

  openPreventionActivity(): void {
    this.showPreventionActivitySubpage.set(true);
    this.activityItems.set([]);
    this.activityPage.set(0);
    this.activityHasMore.set(true);
    this.activityTotal.set(0);
    this.loadMoreActivity();
  }
  closePreventionActivity(): void { this.showPreventionActivitySubpage.set(false); }

  openPreventionCatalog(): void { this.showPreventionCatalogSubpage.set(true); }
  closePreventionCatalog(): void {
    this.closeOfferFormSubpageGuarded(() => {
      this.showPreventionCatalogSubpage.set(false);
      this.editingOfferId.set(null);
      this.showAddOfferSubpage.set(false);
      this.showOfferFormSubpage.set(false);
      this.formReadOnly.set(false);
    });
  }

  /** "Ajouter une offre" -- incruste le catalogue du GROUPE (`app-prevention-site-add-offer`), jamais
   * la Bibliothèque Waitora : un site ne pioche que dans le catalogue de son propre groupe, jamais
   * directement dans Waitora (architecture verrouillée). */
  readonly showAddOfferSubpage = signal(false);
  openAddOffer(): void { this.showAddOfferSubpage.set(true); }
  closeAddOfferSubpage(): void {
    this.closeOfferFormSubpageGuarded(() => {
      this.showAddOfferSubpage.set(false);
      this.showOfferFormSubpage.set(false);
    });
  }

  /**
   * Garde "modifications non enregistrées" pour le formulaire d'offre EN MODE INCRUSTÉ (fix #4,
   * revue technique 2026-09-03) : `app-offer-form-page[embedded]=true` ne passe jamais par le Router
   * (voir `OfferFormPageComponent#hasUnsavedChanges`/`isDirty`), donc ses 3 sorties possibles depuis
   * CETTE page (flèche retour du fil d'Ariane, liens du fil d'Ariane via `closeAddOfferSubpage`/
   * `closePreventionCatalog`, et `(back)` du formulaire lui-même via `closeOfferFormSubpage`) doivent
   * toutes passer par CE garde-fou avant de fermer -- même protection que la route autonome
   * `/prevention-offres/:offerId`, jamais deux mécanismes divergents pour le même formulaire. No-op
   * (ferme directement) si la sous-page offre n'est pas ouverte ou n'a rien de non enregistré.
   */
  private readonly offerFormPageRef = viewChild(OfferFormPageComponent);
  readonly showLeaveOfferFormConfirm = signal(false);
  private pendingCloseOfferForm: (() => void) | null = null;

  private closeOfferFormSubpageGuarded(doClose: () => void): void {
    if (this.showOfferFormSubpage() && this.offerFormPageRef()?.isDirty()) {
      this.pendingCloseOfferForm = doClose;
      this.showLeaveOfferFormConfirm.set(true);
      // app-confirm-modal est déclarée dans CE template, donc DESCENDANTE de #w-main (router-outlet)
      // -- inertBackground:false, sinon `inert` sur #w-main rendrait la modale elle-même inerte.
      lockPageScroll({ inertBackground: false });
      return;
    }
    doClose();
  }

  confirmLeaveOfferForm(): void {
    this.showLeaveOfferFormConfirm.set(false);
    unlockPageScroll();
    const doClose = this.pendingCloseOfferForm;
    this.pendingCloseOfferForm = null;
    doClose?.();
  }

  cancelLeaveOfferForm(): void {
    this.showLeaveOfferFormConfirm.set(false);
    unlockPageScroll();
    this.pendingCloseOfferForm = null;
  }

  /** Offre déjà créée dans le catalogue du groupe (par un autre site, ou par ce site) que l'utilisateur
   * veut activer pour CE site -- ferme l'écran d'ajout et ouvre directement l'édition de sa ligne dans
   * "Offres du site" (voir `offersToShowInSiteList`), jamais un nouveau POST /offers. Le prix étant
   * obligatoire par site (règle métier, jamais de valeur par défaut inventée -- un prix copié d'un
   * autre site serait faux), on ne peut pas activer en un clic : on ouvre donc le formulaire déjà
   * basculé sur "Actif" (il ne reste plus qu'à saisir le prix et Enregistrer) et on scrolle la ligne
   * en vue, pour que "Ajouter à ce site" ressemble le plus possible à un ajout direct malgré cette
   * contrainte. */
  onActivateOfferForCurrentSite(offerId: string): void {
    this.closeAddOfferSubpage();
    const offer = (this.preventionCatalog() ?? []).find(o => o.offerId === offerId);
    if (!offer) return;
    this.startEditOffer(offer);
    this.editOfferActive.set(true);
    setTimeout(() => {
      document.getElementById(`offer-row-${offerId}`)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    });
  }

  /** Niveau 3 : formulaire de création (libre) ou de consultation lecture seule d'une offre existante
   * du catalogue du groupe -- incruste app-offer-form-page (même composant que
   * /prevention-offres/nouvelle et /prevention-offres/:offerId, aucune logique dupliquée). Toujours
   * atteint DEPUIS l'écran d'ajout (`showAddOfferSubpage` reste vrai en dessous). */
  readonly showOfferFormSubpage = signal(false);
  readonly configuringOfferId = signal<string | null>(null);
  readonly configuringOfferName = signal<string | null>(null);
  readonly creatingFromTemplateCode = signal<string | null>(null);
  /** `true` pour "Détails" (offre déjà existante du catalogue du groupe, jamais éditable depuis un
   * site) -- `false` pour une création libre, où le site est bien l'auteur de l'offre. */
  readonly formReadOnly = signal(false);

  /** "Détails" -- consultation seule d'une offre déjà existante du catalogue du groupe. */
  openOfferDetails(offerId: string): void {
    const offer = (this.preventionCatalog() ?? []).find(o => o.offerId === offerId);
    this.configuringOfferId.set(offerId);
    this.configuringOfferName.set(offer?.name ?? null);
    this.creatingFromTemplateCode.set(null);
    this.formReadOnly.set(true);
    this.showOfferFormSubpage.set(true);
  }
  /** "Créer une nouvelle offre" -- création libre à partir des bilans, jamais de la Bibliothèque
   * Waitora depuis un site. L'offre créée rejoint le catalogue du groupe, sans être activée nulle
   * part par défaut. */
  openCreateOfferFormBlank(): void {
    this.configuringOfferId.set(null);
    this.configuringOfferName.set(null);
    this.creatingFromTemplateCode.set(null);
    this.formReadOnly.set(false);
    this.showOfferFormSubpage.set(true);
  }
  /** Bascule le formulaire embarqué de "création" vers "édition" de l'offre qui vient d'être créée,
   * sans quitter /site (remplace la navigation vers /prevention-offres/:offerId hors embarquement).
   * Rafraîchit aussi le catalogue de ce site pour que la nouvelle offre apparaisse immédiatement dans
   * le compteur/la liste "Offres du site" au retour, sans recharger la page. */
  onOfferCreated(event: { offerId: string; name: string }): void {
    this.configuringOfferId.set(event.offerId);
    this.configuringOfferName.set(event.name);
    this.creatingFromTemplateCode.set(null);
    this._preventionCatalogRefresh.update(n => n + 1);
  }
  closeOfferFormSubpage(): void {
    this.closeOfferFormSubpageGuarded(() => {
      this.showOfferFormSubpage.set(false);
      this.formReadOnly.set(false);
    });
  }

  // ── Sous-page Activité : liste paginée "Charger plus" ──
  readonly activityItems = signal<ActivityItem[]>([]);
  readonly activityPage = signal(0);
  readonly activityHasMore = signal(true);
  readonly activityTotal = signal(0);
  readonly activityLoading = signal(false);

  loadMoreActivity(): void {
    const id = this.session.selectedId();
    if (!id || this.activityLoading() || !this.activityHasMore()) return;
    this.activityLoading.set(true);
    this.preventionSvc.getActivity(id, this.activityPage(), 20).subscribe({
      next: (result) => {
        this.activityItems.update(items => [...items, ...result.items]);
        this.activityHasMore.set(result.hasMore);
        this.activityTotal.set(result.totalElements);
        this.activityPage.update(n => n + 1);
        this.activityLoading.set(false);
      },
      error: () => {
        this.activityHasMore.set(false);
        this.activityLoading.set(false);
        this.toast.show('Erreur', 'error', 4000, 'Impossible de charger l\'activité');
      },
    });
  }

  // ── Sous-page "Offres du site" : édition d'une offre (Lot 9.0-E.E.G) -- ligne extensible, un seul
  // éditeur ouvert à la fois (même pattern que showPreventionCatalogSubpage/editingOfferId déjà en
  // place ; jamais de composition/nom/description/bilans éditables ici, ORGANISATION-scopés). ──
  readonly editingOfferId = signal<string | null>(null);
  /** Établissement pour lequel l'édition en cours a été ouverte (capturé par `startEditOffer`,
   *  JAMAIS relu depuis `session.selectedId()` pendant l'édition) -- le sélecteur de site en tête
   *  de page reste cliquable pendant toute l'édition (aucune modale ne le bloque), donc `saveOffer`/
   *  `confirmRemoveOffer` DOIVENT comparer ce site capturé au site courant avant d'écrire, sous
   *  peine d'appliquer un prix/des conditions périmés à un AUTRE établissement (revue technique du
   *  2026-09-03, BLOCKER #1). */
  readonly editingEstablishmentId = signal<string | null>(null);
  readonly editOfferActive = signal(false);
  readonly editOfferPrice = signal<number | null>(null);
  readonly editOfferCurrency = signal<string>('EUR');
  readonly editOfferFeasible = signal(true);
  readonly editOfferConditions = signal('');
  readonly editOfferContactRole = signal<PatientContactRole | null>(null);
  readonly editOfferCommercialPreference = signal(false);
  readonly savingOffer = signal(false);
  readonly offerSiteCurrencies = OFFER_SITE_CURRENCIES;

  /** Composition d'une offre affichée directement (noms des bilans joints par " + "), jamais un simple compteur -- lecture seule, jamais couplé à editingOfferId. */
  bilanNames(offer: EstablishmentOfferCatalog): string {
    return offer.bilans.map(bilan => bilan.name).join(' + ');
  }

  /** URL PUBLIQUE cache-bustée (un `<img src>` n'envoie jamais l'en-tête Authorization) -- même
   * idiome que photoUrl() sur OfferFormPageComponent/SiteComponent#photoUrl (établissement). */
  getOfferPhotoUrl(offer: EstablishmentOfferCatalog): string {
    return this.offersSvc.getPhotoUrl(offer.offerId, offer.photoUpdatedAt);
  }

  startEditOffer(offer: EstablishmentOfferCatalog): void {
    this.editingOfferId.set(offer.offerId);
    this.editingEstablishmentId.set(this.session.selectedId());
    this.editOfferActive.set(offer.activeForEstablishment);
    this.editOfferPrice.set(offer.price);
    this.editOfferCurrency.set(offer.currency ?? 'EUR');
    this.editOfferFeasible.set(offer.immediateFeasibilityDefault ?? true);
    this.editOfferConditions.set(offer.conditions ?? '');
    this.editOfferContactRole.set(offer.patientContactRole);
    this.editOfferCommercialPreference.set(offer.commercialPreference);
  }

  cancelEditOffer(): void { this.editingOfferId.set(null); this.editingEstablishmentId.set(null); }

  onEditOfferPriceInput(value: string): void {
    const n = parseFloat(value.replace(',', '.'));
    this.editOfferPrice.set(isNaN(n) ? null : n);
  }

  onEditOfferCurrencyChanged(value: string): void {
    this.editOfferCurrency.set(value);
  }

  onEditOfferContactRoleChanged(value: string): void {
    this.editOfferContactRole.set((value === '' ? null : value) as PatientContactRole | null);
  }

  onToggleEditOfferCommercialPreference(): void {
    this.editOfferCommercialPreference.set(!this.editOfferCommercialPreference());
  }

  saveOffer(): void {
    const establishmentId = this.session.selectedId();
    const offerId = this.editingOfferId();
    if (!establishmentId || !offerId || this.savingOffer()) return;

    // Garde anti-corruption cross-site (BLOCKER #1, revue technique 2026-09-03) : le sélecteur de
    // site en tête de page reste cliquable pendant toute l'édition, donc `session.selectedId()` a
    // pu changer depuis `startEditOffer()` sans que `editingOfferId`/les champs édités ne soient
    // réinitialisés (voir aussi l'effect ci-dessous, qui ferme l'éditeur au changement de site --
    // cette vérification synchrone reste la garde AUTORITATIVE, indépendante du timing de l'effect).
    // N'écrit JAMAIS sur un autre établissement que celui pour lequel l'édition a été ouverte.
    if (establishmentId !== this.editingEstablishmentId()) {
      this.editingOfferId.set(null);
      this.editingEstablishmentId.set(null);
      this.toast.show('Erreur', 'error', 5000, this.translate.instant('site.prevention.offer.establishmentChangedError'));
      return;
    }

    const active = this.editOfferActive();
    if (active && (this.editOfferPrice() == null || this.editOfferPrice()! < 0)) {
      this.toast.show('Erreur', 'error', 4000, this.translate.instant('site.prevention.offer.priceRequired'));
      return;
    }

    this.savingOffer.set(true);
    this.preventionSvc.setEstablishmentOfferAvailability(establishmentId, offerId, {
      active,
      price: this.editOfferPrice(),
      currency: this.editOfferCurrency(),
      immediateFeasibilityDefault: this.editOfferFeasible(),
      conditions: this.editOfferConditions().trim() || null,
      patientContactRole: this.editOfferContactRole(),
      commercialPreference: this.editOfferCommercialPreference(),
    }).subscribe({
      next: () => {
        this.savingOffer.set(false);
        // Fix #3 (revue technique 2026-09-03) : ne referme QUE si c'est toujours cette offre-ci qui
        // est en cours d'édition -- un save lent (offre A) ne doit jamais écraser une édition plus
        // récente ouverte entre-temps sur une AUTRE ligne (offre B) pendant que la requête de A était
        // en vol (perte silencieuse de la saisie de B sinon).
        if (this.editingOfferId() === offerId) {
          this.editingOfferId.set(null);
          this.editingEstablishmentId.set(null);
        }
        this._preventionCatalogRefresh.update(n => n + 1);
        this.toast.show('C\'est noté. Merci 👋', 'success', 4000, this.translate.instant('site.prevention.offer.saveSuccess'));
      },
      error: (err) => {
        this.savingOffer.set(false);
        this.toast.show('Erreur', 'error', 4000, err?.error?.message || this.translate.instant('site.prevention.offer.saveError'));
      },
    });
  }

  /** Retirer une offre du site (icône poubelle, Lot 9.0-E.E.G ter) -- ne supprime JAMAIS l'Offer du
   * catalogue du groupe (org-wide), seulement sa disponibilité pour CET établissement : même
   * endpoint/payload que `saveOffer`, `active: false` uniquement. Toujours confirmé (modale), le
   * message insistant puisque toute offre listée ici est forcément active (la liste "Offres du
   * site" ne montre que `activeOffers()`, jamais le catalogue org-wide brut). */
  readonly removeOfferTarget = signal<EstablishmentOfferCatalog | null>(null);
  /** Même garde que `editingEstablishmentId` pour `saveOffer` (BLOCKER #1) : établissement capturé
   *  à l'ouverture de la modale, revérifié avant écriture dans `confirmRemoveOffer`. */
  readonly removeOfferEstablishmentId = signal<string | null>(null);
  readonly removingOffer = signal(false);
  readonly removeOfferError = signal<string | null>(null);

  openRemoveOfferConfirm(offer: EstablishmentOfferCatalog, event: Event): void {
    event.stopPropagation();
    this.removeOfferTarget.set(offer);
    this.removeOfferEstablishmentId.set(this.session.selectedId());
    this.removeOfferError.set(null);
    // app-confirm-modal est déclarée dans CE template, donc DESCENDANTE de #w-main (router-outlet)
    // -- inertBackground:false, sinon `inert` sur #w-main rendrait la modale elle-même inerte.
    lockPageScroll({ inertBackground: false });
  }

  closeRemoveOfferConfirm(): void {
    if (this.removingOffer()) return;
    this.removeOfferTarget.set(null);
    this.removeOfferEstablishmentId.set(null);
    this.removeOfferError.set(null);
    unlockPageScroll();
  }

  confirmRemoveOffer(): void {
    const establishmentId = this.session.selectedId();
    const offer = this.removeOfferTarget();
    if (!establishmentId || !offer || this.removingOffer()) return;

    // Garde anti-corruption cross-site (BLOCKER #1, même raisonnement que saveOffer).
    if (establishmentId !== this.removeOfferEstablishmentId()) {
      this.removeOfferTarget.set(null);
      this.removeOfferEstablishmentId.set(null);
      unlockPageScroll();
      this.toast.show('Erreur', 'error', 5000, this.translate.instant('site.prevention.offer.establishmentChangedError'));
      return;
    }

    this.removingOffer.set(true);
    this.removeOfferError.set(null);
    this.preventionSvc.setEstablishmentOfferAvailability(establishmentId, offer.offerId, {
      active: false,
      price: offer.price,
      currency: offer.currency ?? 'EUR',
      immediateFeasibilityDefault: offer.immediateFeasibilityDefault ?? true,
      conditions: offer.conditions,
      patientContactRole: offer.patientContactRole,
      commercialPreference: offer.commercialPreference,
    }).subscribe({
      next: () => {
        this.removingOffer.set(false);
        this.removeOfferTarget.set(null);
        this.removeOfferEstablishmentId.set(null);
        unlockPageScroll();
        if (this.editingOfferId() === offer.offerId) {
          this.editingOfferId.set(null);
          this.editingEstablishmentId.set(null);
        }
        this._preventionCatalogRefresh.update(n => n + 1);
        this.toast.show('C\'est noté. Merci 👋', 'success', 4000, this.translate.instant('site.prevention.offer.removeSuccess'));
      },
      error: (err) => {
        this.removingOffer.set(false);
        this.removeOfferError.set(err?.error?.message || this.translate.instant('site.prevention.offer.removeError'));
      },
    });
  }
}
